import { useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { Users, RefreshCw, Filter, Mail, Phone, MapPin, MessageSquare } from "lucide-react";
import PanelState, { extractApiError } from "@/components/PanelState";

const STATUS_OPTIONS = [
  { value: "new",         label: "New",         tone: "bg-[#e7f4fb] text-[#1c6a99] border-[#c5e0ee]" },
  { value: "contacted",   label: "Contacted",   tone: "bg-[#fff4d6] text-[#8a5b00] border-[#f3d97a]" },
  { value: "qualified",   label: "Qualified",   tone: "bg-[#e6f9f2] text-[#0e8a68] border-[#c1eddc]" },
  { value: "not_a_fit",   label: "Not a Fit",   tone: "bg-[#f5f6f8] text-[#4b5563] border-[#e7e9ee]" },
];
const STATUS_MAP = Object.fromEntries(STATUS_OPTIONS.map((s) => [s.value, s]));

function fmtDateTime(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString([], {
      month: "short", day: "numeric", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * AdminLeads — platform_admin-only lead CRM.
 * Simple table: name/email/phone/business/location/message/date + inline status dropdown.
 * Each status change writes to admin_audit_log (server-side, full history preserved).
 */
export default function AdminLeads() {
  const [leads, setLeads] = useState([]);
  const [loadState, setLoadState] = useState({ status: "loading", error: null });
  const [filter, setFilter] = useState(""); // "" = all, else status value
  const [refreshing, setRefreshing] = useState(false);
  const [savingId, setSavingId] = useState(null);

  const load = useCallback(async () => {
    setRefreshing(true);
    try {
      const params = filter ? { status: filter } : {};
      const { data } = await api.get("/admin/leads", { params });
      setLeads(Array.isArray(data) ? data : []);
      setLoadState({ status: "ok", error: null });
    } catch (err) {
      setLeads([]);
      setLoadState({ status: "error", error: extractApiError(err) });
    } finally {
      setRefreshing(false);
    }
  }, [filter]);

  useEffect(() => {
    load();
    // Mark leads seen when the page loads — clears the sidebar unread badge for
    // this admin. The badge is driven by count(status="new"), so we don't have a
    // separate "seen" pointer; the admin simply changes status to move it out of
    // the "new" bucket. Nothing to POST here.
  }, [load]);

  const updateStatus = async (leadId, newStatus) => {
    setSavingId(leadId);
    try {
      await api.patch(`/admin/leads/${leadId}/status`, { status: newStatus });
      setLeads((rows) => rows.map((r) => r.id === leadId ? {
        ...r,
        status: newStatus,
        status_updated_at: new Date().toISOString(),
      } : r));
    } catch (err) {
      window.alert(extractApiError(err).message || "Could not update status.");
    } finally {
      setSavingId(null);
    }
  };

  const counts = STATUS_OPTIONS.reduce((acc, s) => {
    acc[s.value] = leads.filter((l) => l.status === s.value).length;
    return acc;
  }, {});

  return (
    <div data-testid="admin-leads-page" className="space-y-5">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold mb-2">
            Platform Admin
          </div>
          <h1 className="font-display font-bold text-[26px] sm:text-[32px] tracking-tight text-[#0e1420] inline-flex items-center gap-2.5">
            <Users size={22} strokeWidth={1.6} className="text-[#0072bc]" />
            Leads
          </h1>
          <p className="text-[13.5px] text-[color:var(--cc-ink-muted)] mt-1">
            Demo requests submitted from the public homepage. Status changes are audit-logged.
          </p>
        </div>
        <button
          onClick={load}
          disabled={refreshing}
          data-testid="admin-leads-refresh"
          className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold px-3 py-2 rounded-md border border-[#e7e9ee] text-[#0e1420] hover:bg-[#f5f6f8] disabled:opacity-50"
        >
          <RefreshCw size={13} strokeWidth={1.8} className={refreshing ? "animate-spin" : ""} />
          Refresh
        </button>
      </header>

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="admin-leads-filter">
        <Filter size={13} strokeWidth={1.8} className="text-[color:var(--cc-ink-muted)]" />
        <FilterChip
          label={filter ? "All" : `All · ${leads.length}`}
          active={filter === ""}
          onClick={() => setFilter("")}
          testId="filter-all"
        />
        {STATUS_OPTIONS.map((s) => (
          <FilterChip
            key={s.value}
            label={`${s.label} · ${counts[s.value] ?? 0}`}
            active={filter === s.value}
            onClick={() => setFilter(s.value)}
            testId={`filter-${s.value}`}
          />
        ))}
      </div>

      <PanelState
        status={loadState.status}
        error={loadState.error}
        isEmpty={leads.length === 0}
        onRetry={load}
        emptyTitle={filter ? `No leads with status "${STATUS_MAP[filter]?.label ?? filter}" yet.` : "No leads yet"}
        emptyBody={filter
          ? "Change the filter above to see other statuses."
          : "When someone submits the demo form on the homepage, they'll appear here."}
        testIdPrefix="admin-leads"
      />

      {loadState.status === "ok" && leads.length > 0 && (
        <div className="bg-white border border-[#e7e9ee] rounded-xl overflow-hidden">
          <ul className="divide-y divide-[#eef1f6]">
            {leads.map((l) => (
              <li key={l.id} className="px-4 sm:px-5 py-4" data-testid={`lead-row-${l.id}`}>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-baseline gap-2 flex-wrap">
                      <div className="text-[15px] font-semibold text-[#0e1420]">{l.name}</div>
                      <div className="text-[12.5px] text-[color:var(--cc-ink-muted)] font-mono-data">
                        · {l.business_name}
                      </div>
                    </div>
                    <div className="text-[12px] text-[color:var(--cc-ink-muted)] mt-1.5 flex items-center gap-4 flex-wrap">
                      <a href={`mailto:${l.email}`} className="inline-flex items-center gap-1 hover:text-[#0072bc]" data-testid={`lead-email-${l.id}`}>
                        <Mail size={11} strokeWidth={1.8} /> {l.email}
                      </a>
                      {l.phone && (
                        <a href={`tel:${l.phone}`} className="inline-flex items-center gap-1 hover:text-[#0072bc]">
                          <Phone size={11} strokeWidth={1.8} /> {l.phone}
                        </a>
                      )}
                      <span className="inline-flex items-center gap-1">
                        <MapPin size={11} strokeWidth={1.8} /> {l.location}
                      </span>
                    </div>
                    <div className="mt-3 p-3 rounded-md bg-[#fafbfc] border border-[#eef1f6] text-[13px] text-[#0e1420] leading-relaxed flex gap-2 items-start">
                      <MessageSquare size={13} strokeWidth={1.8} className="text-[color:var(--cc-ink-muted)] shrink-0 mt-0.5" />
                      <span className="whitespace-pre-wrap">{l.headache}</span>
                    </div>
                  </div>
                  <div className="text-right shrink-0 min-w-[180px] space-y-2">
                    <div className="text-[10.5px] uppercase tracking-widest text-[color:var(--cc-ink-muted)] font-semibold">
                      Submitted
                    </div>
                    <div className="text-[11.5px] text-[#0e1420] font-mono-data">
                      {fmtDateTime(l.created_at)}
                    </div>
                    <div className="pt-2">
                      <select
                        value={l.status}
                        onChange={(e) => updateStatus(l.id, e.target.value)}
                        disabled={savingId === l.id}
                        data-testid={`lead-status-${l.id}`}
                        className={`text-[11.5px] uppercase tracking-widest font-bold border rounded-md px-2 py-1.5 pr-6 cursor-pointer disabled:opacity-60 ${STATUS_MAP[l.status]?.tone ?? ""}`}
                      >
                        {STATUS_OPTIONS.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                      {l.status_updated_at && (
                        <div className="text-[10px] text-[color:var(--cc-ink-muted)] font-mono-data mt-1.5">
                          changed {fmtDateTime(l.status_updated_at)}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function FilterChip({ label, active, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`text-[11.5px] font-semibold px-2.5 py-1 rounded-md border transition-colors ${
        active
          ? "bg-[#0072bc] text-white border-[#0072bc]"
          : "bg-white text-[#0e1420] border-[#e7e9ee] hover:bg-[#f5f6f8]"
      }`}
    >
      {label}
    </button>
  );
}
